import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Collapse,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  CheckCircle as ApproveIcon,
  Payment as PayIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import moment from 'moment';
import MockDataIndicator from '../../../../Components/Common/MockDataIndicator';
import { commissionEarnings } from '../../../../mockData/financeData';

// Format currency
const formatCurrency = (amount, currency = 'AED') => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

// Status chip configuration
const getStatusConfig = (status) => {
  const configs = {
    PENDING: { color: 'warning', label: 'Pending Approval' },
    APPROVED: { color: 'info', label: 'Approved - Awaiting Payment' },
    PAID: { color: 'success', label: 'Paid' },
    REJECTED: { color: 'error', label: 'Rejected' },
  };
  return configs[status] || { color: 'default', label: status };
};

// Tab Panel Component
const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

// Commission Detail Row
const CommissionDetailRow = ({ commission, onApprove, onPay }) => {
  const statusConfig = getStatusConfig(commission.status);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
      <Grid container spacing={3}>
        {/* Left Column - Commission Summary */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Commission Summary
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Channel Partner:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {commission.channelPartnerName}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Period:
              </Typography>
              <Typography variant="body2">{commission.month}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Total Tickets:
              </Typography>
              <Typography variant="body2">{commission.totalTickets}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Total Gross Profit:
              </Typography>
              <Typography variant="body2">{formatCurrency(commission.totalGrossProfit)}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Commission Rate:
              </Typography>
              <Typography variant="body2">{commission.commissionRate}%</Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                Total Commission:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'success.main' }}
              >
                {formatCurrency(commission.totalCommission)}
              </Typography>
            </Box>

            {commission.status === 'APPROVED' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Approved on: {moment(commission.approvedDate).format('DD MMM YYYY')}
                </Typography>
              </Box>
            )}

            {commission.status === 'PAID' && (
              <>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Approved on: {moment(commission.approvedDate).format('DD MMM YYYY')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Paid on: {moment(commission.paidDate).format('DD MMM YYYY')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Reference: {commission.paymentReference}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Action Buttons */}
          {commission.status === 'PENDING' && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ApproveIcon />}
                onClick={() => onApprove(commission)}
              >
                Approve Commission
              </Button>
            </Box>
          )}

          {commission.status === 'APPROVED' && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<PayIcon />}
                onClick={() => onPay(commission)}
              >
                Process Payment
              </Button>
            </Box>
          )}
        </Grid>

        {/* Right Column - Contributing Tickets */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Contributing Tickets ({commission.tickets.length})
          </Typography>

          {commission.tickets.length > 0 ? (
            <List sx={{ maxHeight: 300, overflow: 'auto', backgroundColor: '#fff', borderRadius: 1 }}>
              {commission.tickets.map((ticket, index) => (
                <ListItem
                  key={ticket.ticketId}
                  divider={index < commission.tickets.length - 1}
                  sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {ticket.ticketNumber}
                    </Typography>
                    <Chip label={formatCurrency(ticket.commission)} size="small" color="success" />
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {ticket.serviceName} - {ticket.customerName}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Profit: {formatCurrency(ticket.grossProfit)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {moment(ticket.completedDate).format('DD MMM YYYY')}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">No ticket details available for paid commission</Alert>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

// Commission Row Component
const CommissionRow = ({ commission, onApprove, onPay }) => {
  const [open, setOpen] = useState(false);
  const statusConfig = getStatusConfig(commission.status);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>

        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {commission.channelPartnerName}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2">{commission.month}</Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">{commission.totalTickets}</Typography>
        </TableCell>

        <TableCell align="right">
          <Typography variant="body2">{formatCurrency(commission.totalGrossProfit)}</Typography>
        </TableCell>

        <TableCell align="right">
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
            {formatCurrency(commission.totalCommission)}
          </Typography>
        </TableCell>

        <TableCell>
          <Chip label={statusConfig.label} color={statusConfig.color} size="small" />
        </TableCell>

        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)} title="View Details">
            <ViewIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <CommissionDetailRow commission={commission} onApprove={onApprove} onPay={onPay} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Payment Confirmation Dialog
const PaymentDialog = ({ open, onClose, commission, onConfirm }) => {
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [notes, setNotes] = useState('');

  const handleConfirm = () => {
    onConfirm({
      commissionId: commission.id,
      paymentReference,
      paymentMethod,
      notes,
      amount: commission.totalCommission,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Process Commission Payment</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Channel Partner:</strong> {commission?.channelPartnerName}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Amount:</strong> {formatCurrency(commission?.totalCommission || 0)}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Reference"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="e.g., PAY-COMM-001"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                placeholder="e.g., Bank Transfer, Cheque"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes (Optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes about this payment"
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            This will mark the commission as paid and record the payment in the system.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          disabled={!paymentReference || !paymentMethod}
        >
          Confirm Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Component
const CommissionPayments = () => {
  const [commissions, setCommissions] = useState([]);
  const [isMockData, setIsMockData] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);

  useEffect(() => {
    // In production: AppService.get('v1/channel-partner/commissions').then(...)
    setCommissions(commissionEarnings);
    setIsMockData(true);
  }, []);

  const handleApprove = (commission) => {
    // In production: AppService.post(`v1/channel-partner/commission/${commission.id}/approve`)
    setCommissions((prev) =>
      prev.map((c) =>
        c.id === commission.id
          ? { ...c, status: 'APPROVED', approvedDate: new Date().toISOString() }
          : c
      )
    );
    alert(`Commission for ${commission.channelPartnerName} has been approved (Mock Mode)`);
  };

  const handlePay = (commission) => {
    setSelectedCommission(commission);
    setPaymentDialogOpen(true);
  };

  const handleConfirmPayment = (paymentData) => {
    // In production: AppService.post(`v1/channel-partner/commission/${paymentData.commissionId}/pay`, paymentData)
    setCommissions((prev) =>
      prev.map((c) =>
        c.id === paymentData.commissionId
          ? {
              ...c,
              status: 'PAID',
              paidDate: new Date().toISOString(),
              paymentReference: paymentData.paymentReference,
            }
          : c
      )
    );
    setPaymentDialogOpen(false);
    setSelectedCommission(null);
    alert(`Payment processed successfully (Mock Mode)\nReference: ${paymentData.paymentReference}`);
  };

  const pendingCommissions = commissions.filter((c) => c.status === 'PENDING');
  const approvedCommissions = commissions.filter((c) => c.status === 'APPROVED');
  const paidCommissions = commissions.filter((c) => c.status === 'PAID');

  const totalPending = pendingCommissions.reduce((sum, c) => sum + c.totalCommission, 0);
  const totalApproved = approvedCommissions.reduce((sum, c) => sum + c.totalCommission, 0);
  const totalPaid = paidCommissions.reduce((sum, c) => sum + c.totalCommission, 0);

  const getFilteredCommissions = () => {
    if (tabValue === 0) return pendingCommissions;
    if (tabValue === 1) return approvedCommissions;
    if (tabValue === 2) return paidCommissions;
    return commissions;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Channel Partner Commissions
        </Typography>
        <MockDataIndicator isMock={isMockData} />
      </Box>

      {isMockData && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Mock Data Mode:</strong> Commission actions are simulated. Backend integration pending.
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Pending Approval
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#e65100', mt: 1 }}>
                {formatCurrency(totalPending)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {pendingCommissions.length} partner(s)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Approved - Awaiting Payment
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1565c0', mt: 1 }}>
                {formatCurrency(totalApproved)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {approvedCommissions.length} partner(s)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Paid This Month
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32', mt: 1 }}>
                {formatCurrency(totalPaid)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {paidCommissions.length} partner(s)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label={`Pending (${pendingCommissions.length})`} />
            <Tab label={`Approved (${approvedCommissions.length})`} />
            <Tab label={`Paid (${paidCommissions.length})`} />
            <Tab label={`All (${commissions.length})`} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={tabValue}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>Channel Partner</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell align="center">Tickets</TableCell>
                  <TableCell align="right">Gross Profit</TableCell>
                  <TableCell align="right">Commission</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell width={80}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredCommissions().map((commission) => (
                  <CommissionRow
                    key={commission.id}
                    commission={commission}
                    onApprove={handleApprove}
                    onPay={handlePay}
                  />
                ))}

                {getFilteredCommissions().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No commissions in this category
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* Payment Dialog */}
      {selectedCommission && (
        <PaymentDialog
          open={paymentDialogOpen}
          onClose={() => setPaymentDialogOpen(false)}
          commission={selectedCommission}
          onConfirm={handleConfirmPayment}
        />
      )}
    </Box>
  );
};

export default CommissionPayments;
